import type { UrlErrorCode } from './lib/url';

export type Locale = 'vi' | 'en';

export type ToastKey =
  | 'ready'
  | 'copied'
  | 'copyFailed'
  | 'downloaded'
  | 'shareUnavailable'
  | 'shareFailed';

type FeatureCopy = {
  title: string;
  detail: string;
};

type TranslationCopy = {
  meta: {
    title: string;
    description: string;
  };
  skipLink: string;
  header: {
    aria: string;
    home: string;
    privacy: string;
    language: string;
    vietnamese: string;
    english: string;
  };
  hero: {
    eyebrow: string;
    leading: string;
    accent: string;
    description: string;
  };
  generator: {
    aria: string;
    kicker: string;
    title: string;
    label: string;
    placeholder: string;
    helper: string;
    generating: string;
    update: string;
    generate: string;
    tip: string;
    privacyTitle: string;
    privacyDetail: string;
  };
  preview: {
    kicker: string;
    title: string;
    ready: string;
    emptyTitle: string;
    emptyDescription: string;
    actionsAria: string;
    download: string;
    copy: string;
    share: string;
    qrAlt: (url: string) => string;
  };
  features: {
    aria: string;
    items: [FeatureCopy, FeatureCopy, FeatureCopy];
  };
  footer: string;
  errors: Record<UrlErrorCode, string>;
  resultError: string;
  toasts: Record<ToastKey, string>;
  share: {
    title: string;
    imageText: (url: string) => string;
    linkText: string;
  };
};

export const translations: Record<Locale, TranslationCopy> = {
  vi: {
    meta: {
      title: 'EasyQR — Tạo mã QR từ liên kết',
      description: 'Biến mọi liên kết thành mã QR sắc nét, dễ quét ngay trong trình duyệt.',
    },
    skipLink: 'Đi đến trình tạo mã QR',
    header: {
      aria: 'Đầu trang EasyQR',
      home: 'Trang chủ EasyQR',
      privacy: 'Riêng tư ngay từ thiết kế',
      language: 'Chọn ngôn ngữ',
      vietnamese: 'Tiếng Việt',
      english: 'English',
    },
    hero: {
      eyebrow: 'Nhanh, riêng tư, miễn phí',
      leading: 'Biến liên kết',
      accent: 'thành mã QR dễ quét.',
      description:
        'Dán liên kết và nhận mã QR sắc nét chỉ trong vài giây. Không tải dữ liệu lên máy chủ—mọi thứ diễn ra ngay trong trình duyệt.',
    },
    generator: {
      aria: 'Trình tạo mã QR',
      kicker: 'Thêm điểm đến',
      title: 'Dán liên kết của bạn',
      label: 'Địa chỉ website',
      placeholder: 'https://example.com',
      helper: 'EasyQR sẽ tự thêm https:// nếu còn thiếu.',
      generating: 'Đang tạo mã QR…',
      update: 'Cập nhật mã QR',
      generate: 'Tạo mã QR',
      tip: 'Mẹo: nhấn Enter để tạo.',
      privacyTitle: 'Liên kết luôn thuộc về bạn.',
      privacyDetail: 'EasyQR không gửi đi hoặc lưu nội dung của bạn.',
    },
    preview: {
      kicker: 'Sẵn sàng sử dụng',
      title: 'Mã QR của bạn',
      ready: 'Sẵn sàng',
      emptyTitle: 'Mã QR sẽ xuất hiện tại đây',
      emptyDescription: 'Nhập liên kết rồi tạo mã QR để mọi người quét bằng camera.',
      actionsAria: 'Thao tác với mã QR',
      download: 'Tải ảnh PNG',
      copy: 'Sao chép liên kết',
      share: 'Chia sẻ',
      qrAlt: (url) => `Mã QR mở liên kết ${url}`,
    },
    features: {
      aria: 'Lợi ích của EasyQR',
      items: [
        { title: 'Không tải dữ liệu', detail: 'Tạo ngay trên thiết bị' },
        { title: 'Tối ưu cho di động', detail: 'Dễ quét và chia sẻ' },
        { title: 'Chất lượng cao', detail: 'Ảnh PNG sắc nét 720px' },
      ],
    },
    footer: 'Đưa link vào. Nhận QR ngay.',
    errors: {
      empty: 'Hãy dán một liên kết trước khi tiếp tục.',
      invalid: 'Liên kết chưa đúng. Hãy thử dạng example.com/trang.',
      unsupported: 'Chỉ hỗ trợ liên kết bắt đầu bằng http:// hoặc https://.',
    },
    resultError: 'Không thể tạo mã QR. Hãy kiểm tra liên kết rồi thử lại.',
    toasts: {
      ready: 'Mã QR đã sẵn sàng để quét.',
      copied: 'Đã sao chép liên kết.',
      copyFailed: 'Không thể sao chép. Hãy chọn và sao chép liên kết thủ công.',
      downloaded: 'Đã tải ảnh PNG chất lượng cao.',
      shareUnavailable: 'Thiết bị này chưa hỗ trợ chia sẻ. Hãy tải ảnh PNG.',
      shareFailed: 'Không thể chia sẻ. Bạn vẫn có thể tải ảnh PNG.',
    },
    share: {
      title: 'Mã QR từ EasyQR',
      imageText: (url) => `Quét mã QR này để mở ${url}`,
      linkText: 'Mở liên kết này:',
    },
  },
  en: {
    meta: {
      title: 'EasyQR — Turn links into QR codes',
      description: 'Turn any web link into a crisp, scannable QR code right in your browser.',
    },
    skipLink: 'Skip to QR generator',
    header: {
      aria: 'EasyQR header',
      home: 'EasyQR home',
      privacy: 'Private by design',
      language: 'Choose language',
      vietnamese: 'Tiếng Việt',
      english: 'English',
    },
    hero: {
      eyebrow: 'Fast, private, free',
      leading: 'Make any link',
      accent: 'easy to scan.',
      description:
        'Paste a link and get a crisp QR code in seconds. Nothing is uploaded—everything happens right in your browser.',
    },
    generator: {
      aria: 'QR code generator',
      kicker: 'Add your destination',
      title: 'Paste your link',
      label: 'Website URL',
      placeholder: 'https://example.com',
      helper: 'A missing https:// will be added for you.',
      generating: 'Creating your QR…',
      update: 'Update QR code',
      generate: 'Generate QR code',
      tip: 'Tip: press Enter to generate.',
      privacyTitle: 'Your link stays yours.',
      privacyDetail: 'EasyQR does not send or store your content.',
    },
    preview: {
      kicker: 'Ready to use',
      title: 'Your QR code',
      ready: 'Ready',
      emptyTitle: 'Your code will appear here',
      emptyDescription: 'Enter a link, then generate a QR code anyone can scan with their camera.',
      actionsAria: 'QR code actions',
      download: 'Download PNG',
      copy: 'Copy link',
      share: 'Share',
      qrAlt: (url) => `QR code that opens ${url}`,
    },
    features: {
      aria: 'EasyQR benefits',
      items: [
        { title: 'No uploads', detail: 'Generated locally' },
        { title: 'Made for mobile', detail: 'Easy to scan and share' },
        { title: 'High quality', detail: 'Sharp 720px PNG' },
      ],
    },
    footer: 'Links in. QR codes out.',
    errors: {
      empty: 'Paste a link first, then try again.',
      invalid: 'That link does not look right. Try something like example.com/page.',
      unsupported: 'Use a web link that begins with http:// or https://.',
    },
    resultError: 'We could not create the QR code. Check the link and try again.',
    toasts: {
      ready: 'Your QR code is ready to scan.',
      copied: 'Link copied to your clipboard.',
      copyFailed: 'Copy did not work. Select the link and copy it manually.',
      downloaded: 'High-resolution PNG downloaded.',
      shareUnavailable: 'Sharing is not available here. Download the PNG instead.',
      shareFailed: 'Sharing did not work. You can still download the PNG.',
    },
    share: {
      title: 'QR code from EasyQR',
      imageText: (url) => `Scan this QR code to open ${url}`,
      linkText: 'Open this link:',
    },
  },
};
